import React, { useMemo, useEffect, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';

import { BarCode, PhysicalCard } from '../../components/custom-icons';

import { EGiftTabPanel } from './components/EGiftTabPanel';
import { PhysicalTabPanel } from './components/PhysicalTabPanel';
import { ReloadTabPanel } from './components/ReloadTabPanel';
import { Tab, TabProps, TabPill } from './components/ProductDetailScreenTab';
import { ProductAmountInputFormData } from './components/ProductAmountInput';

import { Product, ProductType } from 'common/api/e-comm/models/Product';
import { createStyle, createStyles } from 'utils/createStyle';
import { ReloadIcon } from '@radix-ui/react-icons';
import { useProductRebate } from 'hooks/useProductRebate';
import { CartResponse } from 'common/api/e-comm/cart/lineitem/models/CartItems';
import { APIResponse } from 'common/api/models';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';
import { EarnTypeConstructorFilter } from 'common/api/constructor/util';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { AffiliateTabPanel } from './components/AffiliateTabPanel';
import {
    Route,
    Switch,
    generatePath,
    useHistory,
    useParams,
    matchPath,
    useLocation,
} from 'react-router-dom';
import { Routes } from 'routes';
import { ProductDetailsScreenParams } from './components/utils/types';
import { selectAffiliateEnabledWeb } from 'common/features/store/duck/ffs';
import { Helmet } from 'react-helmet-async';
import { colors } from 'styles/settings';

export interface ProductDetailMainTabProps {
    products: Product[];
    selectedTab: ProductType;
    onSelectedTabChange: (tabId: ProductType) => void;
    handleAddItemToCart: (
        product: Product,
        amount: ProductAmountInputFormData
    ) => Promise<APIResponse<CartResponse, null>>;
    brandId: number;
    brandRebate: number;
    brandName: string;
    hidePhysicalTab?: boolean;
    brandImageUrl: string;
}

export interface ProductDetailMainAffiliateTabProps {
    products: Product[];
    selectedTab?: ProductType;
    onSelectedTabChange: (tabId: ProductType) => void;
    handleAddItemToCart: (
        product: Product,
        amount: ProductAmountInputFormData
    ) => Promise<APIResponse<CartResponse, null>>;
    brandId: number;
    brandRebate: number;
    brandName: string;
    hidePhysicalTab?: boolean;
    brandImageUrl: string;
    selectedEarnTypeTab: EarnTypeConstructorFilter;
    onSelectedEarnTypeTabChange: (tabId: EarnTypeConstructorFilter) => void;
    constructorBrand: Brand;
    isAffiliateOnlyBrand?: boolean;
    isGiftCardOnlyBrand: boolean;
}

const styles = createStyles({
    tabs: createStyle('flex flex-col w-full mt-3', {
        maxWidth: '780px',
    }),
    tabList: createStyle('flex flex-shrink-0 border-b border-solid border-grey-light gap-2', {
        marginBottom: '10px',
    }),
    tabsContent: createStyle('text-darkest-grey text-sm lg:text-base', {
        minHeight: '180px',
    }),
    tabListPill: createStyle('flex flex-shrink-0', {
        marginBottom: '10px',
        gap: '1px',
    }),
    tabsContentShopOnline: createStyle('text-grey-darkest pt-1.5 text-sm lg:text-base', {
        minHeight: '180px',
    }),
});

export const ProductDetailMainTab = ({
    products,
    brandId,
    brandName,
    brandImageUrl,
    selectedTab,
    onSelectedTabChange,
    handleAddItemToCart,
    hidePhysicalTab,
}: ProductDetailMainTabProps) => {
    const [resetKey, setResetKey] = React.useState(0);
    const rawBrandId = useParams<ProductDetailsScreenParams>().id;
    const history = useHistory();
    const { pathname } = useLocation();

    const defaultOrg = useRootSelector((s) => {
        const homeDetails = s.store.home.homeDetails;

        if (homeDetails?.organizations && homeDetails?.defaultOrganization) {
            return homeDetails?.organizations[homeDetails?.defaultOrganization?.id];
        }
    });

    const isShipToCoordinatorEnabled = useRootSelector(selectIsShipToCoordinatorEnabled);

    const canCheckout = Boolean(defaultOrg?.isActive && defaultOrg?.organization?.isActive);
    const eGiftCardProduct =
        products && products.find((prod) => prod.type === ProductType.EGiftCard);
    const physicalCardProduct =
        products && products.find((prod) => prod.type === ProductType.GiftCard);
    const reloadCardProduct = products && products.find((prod) => prod.type === ProductType.Reload);

    const onValueChangeHandler = (name: string) => {
        setResetKey((prev) => prev + 1);
        onSelectedTabChange(name as ProductType);
    };

    useEffect(() => {
        if (matchPath(pathname, { path: Routes.BrandsDetails, exact: true })) {
            const searchParams = new URLSearchParams(location.search);
            history.replace({
                pathname: generatePath(Routes.BrandsGiftCardDetails, { id: `${rawBrandId}` }),
                search: searchParams.toString(),
            });
        }
    }, [pathname, rawBrandId, history, location.search]);

    return (
        <Tabs.Root className={styles.tabs} value={selectedTab} onValueChange={onValueChangeHandler}>
            <p className="text-grey-1 mb-2 text-sm">Available as:</p>
            <Tabs.List className={`pdp_available_as_bar ${styles.tabList}`}>
                {physicalCardProduct && !hidePhysicalTab && (
                    <OfferTab
                        product={physicalCardProduct}
                        selected={ProductType.GiftCard === selectedTab}
                        tabId={ProductType.GiftCard}
                        title="Physical"
                        icon={<PhysicalCard height="1.25rem" className="inline-block mr-2" />}
                    />
                )}
                {eGiftCardProduct && (
                    <OfferTab
                        product={eGiftCardProduct}
                        selected={ProductType.EGiftCard === selectedTab}
                        tabId={ProductType.EGiftCard}
                        title="eGift"
                        icon={<BarCode height="1rem" className="inline-block mr-2" />}
                    />
                )}
                {reloadCardProduct && (
                    <OfferTab
                        product={reloadCardProduct}
                        selected={ProductType.Reload === selectedTab}
                        tabId={ProductType.Reload}
                        title="Reload"
                        icon={<ReloadIcon height="1.25rem" className="inline-block mr-2" />}
                    />
                )}
            </Tabs.List>
            <div className={styles.tabsContent}>
                {eGiftCardProduct && (
                    <EGiftTabPanel
                        value={ProductType.EGiftCard}
                        brandId={brandId}
                        product={eGiftCardProduct}
                        addToCart={handleAddItemToCart}
                        brandName={brandName}
                        brandImageUrl={brandImageUrl}
                        canCheckout={canCheckout}
                        resetKey={resetKey}
                    />
                )}
                {physicalCardProduct && !hidePhysicalTab && (
                    <PhysicalTabPanel
                        brandName={brandName}
                        value={ProductType.GiftCard}
                        product={physicalCardProduct}
                        addToCart={handleAddItemToCart}
                        canCheckout={canCheckout}
                        isShipToCoordinatorEnabled={isShipToCoordinatorEnabled}
                    />
                )}
                {reloadCardProduct && (
                    <ReloadTabPanel
                        value={ProductType.Reload}
                        brandId={brandId}
                        brandName={brandName}
                        product={reloadCardProduct}
                        addToCart={handleAddItemToCart}
                        canCheckout={canCheckout}
                    />
                )}
            </div>
        </Tabs.Root>
    );
};

export const ProductDetailMainTabAffiliate = ({
    products,
    brandId,
    brandName,
    brandImageUrl,
    selectedTab,
    onSelectedTabChange,
    handleAddItemToCart,
    hidePhysicalTab,
    selectedEarnTypeTab,
    onSelectedEarnTypeTabChange,
    constructorBrand,
    isAffiliateOnlyBrand,
    isGiftCardOnlyBrand,
}: ProductDetailMainAffiliateTabProps) => {
    const [resetKey, setResetKey] = React.useState(0);
    const { isAffiliateOfferOnBonus, isOnBonus } = constructorBrand;
    const rawBrandId = useParams<ProductDetailsScreenParams>().id;
    const { pathname } = useLocation();

    const defaultOrg = useRootSelector((s) => {
        const homeDetails = s.store.home.homeDetails;

        if (homeDetails?.organizations && homeDetails?.defaultOrganization) {
            return homeDetails?.organizations[homeDetails?.defaultOrganization?.id];
        }
    });

    const isShipToCoordinatorEnabled = useRootSelector(selectIsShipToCoordinatorEnabled);
    const isAffiliateEnabled = useRootSelector(selectAffiliateEnabledWeb);

    const canCheckout = Boolean(defaultOrg?.isActive && defaultOrg?.organization?.isActive);

    const eGiftCardProduct =
        products && products.find((prod) => prod.type === ProductType.EGiftCard);
    const eGiftCardOnBonus =
        eGiftCardProduct?.bonus || eGiftCardProduct?.variants?.some((item) => item.bonus);
    const physicalCardProduct =
        products && products.find((prod) => prod.type === ProductType.GiftCard);
    const physicalCardOnBonus =
        physicalCardProduct?.bonus || physicalCardProduct?.variants?.some((item) => item.bonus);
    const reloadCardProduct = products && products.find((prod) => prod.type === ProductType.Reload);
    const reloadCardOnBonus =
        reloadCardProduct?.bonus || reloadCardProduct?.variants?.some((item) => item.bonus);
    const productBrandId = eGiftCardProduct?.brandId || reloadCardProduct?.brandId || null;
    const onValueChangeHandler = (name: string) => {
        setResetKey((prev) => prev + 1);
        onSelectedTabChange(name as ProductType);
    };

    const [scrollPosition, setScrollPosition] = useState(0);
    const handleScroll = () => {
        const position = window.scrollY;
        setScrollPosition(position);
    };

    const history = useHistory<any>();
    const onChangeEarnTypeTab = (earnType: string) => {
        const brandName = history.location.state?.brandName || '';
        const backRoute = history.location.state?.backRoute || '';
        onSelectedEarnTypeTabChange(earnType as EarnTypeConstructorFilter);

        handleScroll();

        if (earnType === EarnTypeConstructorFilter.GiftCards) {
            history.replace(generatePath(Routes.BrandsGiftCardDetails, { id: `${rawBrandId}` }), {
                brandName,
                backRoute,
            });
        } else {
            history.replace(generatePath(Routes.BrandsShopOnlineDetails, { id: `${rawBrandId}` }), {
                brandName,
                backRoute,
            });
        }
    };
    const redirectPath = useMemo(
        () =>
            generatePath(
                isAffiliateOnlyBrand
                    ? Routes.BrandsShopOnlineDetails
                    : Routes.BrandsGiftCardDetails,
                { id: `${rawBrandId}` }
            ),
        [rawBrandId, isAffiliateOnlyBrand]
    );

    useEffect(() => {
        window.scrollTo(0, scrollPosition);
    }, [scrollPosition, pathname]);

    useEffect(() => {
        if (matchPath(pathname, { path: Routes.BrandsDetails, exact: true })) {
            history.replace(redirectPath);
        }
    }, [pathname, redirectPath, history]);

    let canonicalUrl = '';
    if (EarnTypeConstructorFilter.GiftCards === selectedEarnTypeTab) {
        canonicalUrl =
            window.location.href.replace('/gift-cards', '').split('?')[0] + '/gift-cards';
    }

    return (
        <Tabs.Root
            className={styles.tabs}
            value={selectedEarnTypeTab}
            onValueChange={onChangeEarnTypeTab}
        >
            <p className="text-brand-dark mb-3 text-lg font-bold">Ways to Earn</p>

            <Tabs.List className={`pdp_available_as_bar ${styles.tabList}`}>
                {!isAffiliateOnlyBrand ? (
                    <Tab
                        offer={isOnBonus ? 'hasOffer' : undefined}
                        selected={EarnTypeConstructorFilter.GiftCards === selectedEarnTypeTab}
                        tabId={EarnTypeConstructorFilter.GiftCards}
                        title="Buy Gift Cards"
                        noUnderline
                    />
                ) : null}
                {isAffiliateEnabled && !isGiftCardOnlyBrand ? (
                    <Tab
                        offer={isAffiliateOfferOnBonus ? 'hasOffer' : undefined}
                        selected={EarnTypeConstructorFilter.ShopOnline === selectedEarnTypeTab}
                        tabId={EarnTypeConstructorFilter.ShopOnline}
                        title="Shop Online"
                        noUnderline
                    />
                ) : null}
            </Tabs.List>
            <Switch>
                <Route path={Routes.BrandsGiftCardDetails}>
                    <Tabs.Root
                        className={styles.tabs}
                        value={selectedTab}
                        onValueChange={onValueChangeHandler}
                    >
                        <p className="text-brand-dark mb-2 text-lg font-bold">Gift Card Types</p>
                        <Tabs.List className={`pdp_available_as_bar ${styles.tabListPill}`}>
                            <Helmet>
                                <link rel="canonical" href={canonicalUrl} />
                            </Helmet>
                            {physicalCardProduct && !hidePhysicalTab && (
                                <TabPill
                                    offer={physicalCardOnBonus ? 'hasOffer' : undefined}
                                    selected={ProductType.GiftCard === selectedTab}
                                    tabId={ProductType.GiftCard}
                                    title="Physical"
                                    icon={
                                        <PhysicalCard
                                            height="1.25rem"
                                            className="inline-block mr-2"
                                        />
                                    }
                                />
                            )}
                            {eGiftCardProduct && (
                                <TabPill
                                    offer={eGiftCardOnBonus ? 'hasOffer' : undefined}
                                    selected={ProductType.EGiftCard === selectedTab}
                                    tabId={ProductType.EGiftCard}
                                    title="eGift"
                                    icon={<BarCode height="1rem" className="inline-block mr-2" />}
                                />
                            )}
                            {reloadCardProduct && (
                                <TabPill
                                    offer={reloadCardOnBonus ? 'hasOffer' : undefined}
                                    selected={ProductType.Reload === selectedTab}
                                    tabId={ProductType.Reload}
                                    title="Reload"
                                    icon={
                                        <ReloadIcon
                                            height="1.125rem"
                                            width="1.125rem"
                                            className="inline-block mr-2"
                                        />
                                    }
                                />
                            )}
                        </Tabs.List>
                        {eGiftCardProduct && (
                            <EGiftTabPanel
                                value={ProductType.EGiftCard}
                                brandId={brandId}
                                product={eGiftCardProduct}
                                addToCart={handleAddItemToCart}
                                brandName={brandName}
                                brandImageUrl={brandImageUrl}
                                canCheckout={canCheckout}
                                resetKey={resetKey}
                            />
                        )}
                        {physicalCardProduct && !hidePhysicalTab && (
                            <PhysicalTabPanel
                                brandName={brandName}
                                value={ProductType.GiftCard}
                                product={physicalCardProduct}
                                addToCart={handleAddItemToCart}
                                canCheckout={canCheckout}
                                isShipToCoordinatorEnabled={isShipToCoordinatorEnabled}
                            />
                        )}
                        {reloadCardProduct && (
                            <ReloadTabPanel
                                value={ProductType.Reload}
                                brandId={brandId}
                                brandName={brandName}
                                product={reloadCardProduct}
                                addToCart={handleAddItemToCart}
                                canCheckout={canCheckout}
                            />
                        )}
                    </Tabs.Root>
                </Route>
                <Route path={Routes.BrandsShopOnlineDetails}>
                    <AffiliateTabPanel
                        brand={constructorBrand}
                        value={selectedEarnTypeTab}
                        contentStyle={styles.tabsContentShopOnline}
                        brandId={productBrandId}
                    />
                </Route>
            </Switch>
        </Tabs.Root>
    );
};

export interface OfferTabProps extends Omit<TabProps, 'offer'> {
    product: Product;
}

export const OfferTab = ({ product, ...props }: OfferTabProps) => {
    const { calculateProductRebate } = useProductRebate();
    const { rebate, isOnBonus } = useMemo(
        () => calculateProductRebate(product),
        [product, calculateProductRebate]
    );

    const uiRebate = useMemo(() => rebate.toPrecision(), [rebate]);

    // TODO: offer is used to style the tab with a different color if it's on bonus
    // nothing else. There's no need for the `earn` text be made.
    const offer = useMemo(() => (isOnBonus ? `Earn ${uiRebate}%` : ''), [isOnBonus, uiRebate]);

    return <Tab {...props} offer={offer} />;
};
